/**
 * `/admins` (Admin-Bereich P6, docs/LOOP-ADMIN-BEREICH.md § P6 — HARTES GATE). Platform-Admins
 * auflisten, ernennen, entfernen — ausschließlich über `GET/POST /api/admin/platform-admins` und
 * `DELETE /api/admin/platform-admins/:stackUserId` (Migration 540). Neu aufgesetzt 26.09.2026 aus
 * `feat-admin-bereich-v2`.
 *
 * Beide Aktionen laufen durch denselben Dialog: die Stack-User-ID muss getippt bestätigt werden
 * (Schutz gegen Vertipper bei der höchsten Rechte-Stufe), die Begründung ist Pflicht und landet im
 * Audit-Log. Letzter-Admin-Schutz und 24-h-Cooldown prüft der Server; seine Meldung steht dann
 * direkt im Dialog.
 */
import { useEffect, useState } from "react";
import { useApi, apiMutate } from "../../hooks/useApi";
import { formatDateTime } from "../../ui/format";
import { Button, Card, EmptyState, ErrorNote, Input, LoadingRows, Modal, PageHeader, Textarea, useToast } from "../../ui/components";

export interface PlatformAdminRow {
  stackUserId: string;
  grantedAt: string;
  grantedBy: string | null;
  email: string | null;
  displayName: string | null;
}

const LABEL = "mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim";

/** Ernennen (`ziel` = null: ID wird eingegeben) oder Entfernen (`ziel` = bekannte Zeile). */
function AdminDialog({
  modus,
  ziel,
  onClose,
  onFertig,
}: {
  modus: "ernennen" | "entfernen" | null;
  ziel: PlatformAdminRow | null;
  onClose: () => void;
  onFertig: () => void;
}) {
  const [stackUserId, setStackUserId] = useState("");
  const [bestaetigung, setBestaetigung] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  useEffect(() => {
    if (!modus) return;
    setStackUserId("");
    setBestaetigung("");
    setReason("");
    setError(null);
    setBusy(false);
  }, [modus, ziel]);

  const entfernen = modus === "entfernen";
  const zielId = entfernen ? (ziel?.stackUserId ?? "") : stackUserId.trim();
  const kannAbsenden = zielId.length > 0 && bestaetigung.trim() === zielId && reason.trim().length > 0 && !busy;

  async function submit() {
    if (!kannAbsenden) return;
    setBusy(true);
    setError(null);
    try {
      if (entfernen) {
        await apiMutate(`/api/admin/platform-admins/${encodeURIComponent(zielId)}`, "DELETE", { reason: reason.trim() });
        toast.success("Platform-Admin entfernt.");
      } else {
        await apiMutate("/api/admin/platform-admins", "POST", { stackUserId: zielId, reason: reason.trim() });
        toast.success("Platform-Admin ernannt.");
      }
      onClose();
      onFertig();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <Modal
      open={modus != null}
      onClose={() => !busy && onClose()}
      width="480px"
      gefahr={entfernen}
      title={entfernen ? "Platform-Admin entfernen" : "Platform-Admin ernennen"}
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      footer={
        <>
          <Button type="button" variant="secondary" disabled={busy} onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant={entfernen ? "danger" : "primary"} disabled={!kannAbsenden}>
            {busy ? "Wird ausgeführt …" : entfernen ? "Entfernen" : "Ernennen"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
          {entfernen ? (
            <>
              <strong>{ziel?.displayName ?? ziel?.email ?? zielId}</strong> verliert den Zugang zum Admin-Bereich. Der letzte
              Platform-Admin kann nicht entfernt werden.
            </>
          ) : (
            <>
              Platform-Admins sehen und ändern alle Mandanten. Die Stack-User-ID findest du in der Mitgliederliste des
              Mandanten.
            </>
          )}
        </p>
        {!entfernen && (
          <label className="block">
            <span className={LABEL}>Stack-User-ID</span>
            <Input type="text" required autoComplete="off" className="font-mono" value={stackUserId} onChange={(e) => setStackUserId(e.target.value)} />
          </label>
        )}
        <label className="block">
          <span className={LABEL}>{entfernen ? "Zur Bestätigung die Stack-User-ID eintippen" : "Stack-User-ID wiederholen"}</span>
          {entfernen && <span className="mb-2 block break-all font-mono text-[12px] text-on-surface-variant dark:text-secondary-fixed-dim">{zielId}</span>}
          <Input
            type="text"
            required
            autoComplete="off"
            className="font-mono"
            value={bestaetigung}
            placeholder={entfernen ? zielId : undefined}
            onChange={(e) => setBestaetigung(e.target.value)}
          />
        </label>
        <label className="block">
          <span className={LABEL}>Begründung</span>
          <Textarea
            required
            className="min-h-[80px]"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={entfernen ? "Warum wird der Zugang entzogen?" : "Warum bekommt diese Person Admin-Rechte?"}
          />
        </label>
        {error && <ErrorNote message={error} />}
      </div>
    </Modal>
  );
}

export function AdminsSeite() {
  const { data, loading, error, reload } = useApi<PlatformAdminRow[]>("/api/admin/platform-admins");
  const [modus, setModus] = useState<"ernennen" | "entfernen" | null>(null);
  const [ziel, setZiel] = useState<PlatformAdminRow | null>(null);

  const zeilen = data ?? [];
  const einziger = zeilen.length <= 1;
  // „Ernannt von“ als Name, wenn der Ernennende selbst in der Liste steht.
  const namen = new Map(zeilen.map((a) => [a.stackUserId, a.displayName ?? a.email]));

  return (
    <>
      <PageHeader
        title="Admins"
        subtitle="Wer den Admin-Bereich der Plattform nutzen darf. Jede Änderung steht im Audit-Log."
        actions={
          <Button
            type="button"
            onClick={() => {
              setZiel(null);
              setModus("ernennen");
            }}
          >
            Admin ernennen
          </Button>
        }
      />

      {error && <ErrorNote message={error} onRetry={reload} />}

      {loading && zeilen.length === 0 ? (
        <Card>
          <LoadingRows rows={3} />
        </Card>
      ) : !error && zeilen.length === 0 ? (
        <Card>
          <EmptyState icon="admin_panel_settings" title="Keine Platform-Admins gefunden" />
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="handy-karten w-full min-w-[640px] border-collapse text-left">
              <thead>
                <tr>
                  <th className="table-header">Person</th>
                  <th className="table-header">Ernannt am</th>
                  <th className="table-header">Ernannt von</th>
                  <th className="table-header" />
                </tr>
              </thead>
              <tbody className="divide-y divide-outline-variant/30 dark:divide-outline/30">
                {zeilen.map((admin) => (
                  <tr key={admin.stackUserId}>
                    <td className="table-cell">
                      <div className="min-w-0">
                        <div className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">
                          {admin.displayName ?? admin.email ?? "Unbekannte Person"}
                        </div>
                        {admin.displayName && admin.email && (
                          <div className="font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">{admin.email}</div>
                        )}
                        <div className="break-all font-mono text-[12px] text-on-surface-variant dark:text-secondary-fixed-dim">{admin.stackUserId}</div>
                      </div>
                    </td>
                    <td className="table-cell whitespace-nowrap font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                      <span className="mr-1 sm:hidden">Ernannt</span>
                      {formatDateTime(admin.grantedAt)}
                    </td>
                    <td className="table-cell font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                      <span className="mr-1 sm:hidden">von</span>
                      {admin.grantedBy && namen.get(admin.grantedBy) ? (
                        namen.get(admin.grantedBy)
                      ) : (
                        <span className="break-all font-mono text-[12px]">{admin.grantedBy ?? "—"}</span>
                      )}
                    </td>
                    <td className="table-cell text-right">
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={einziger}
                        title={einziger ? "Der letzte Platform-Admin kann nicht entfernt werden." : undefined}
                        onClick={() => {
                          setZiel(admin);
                          setModus("entfernen");
                        }}
                      >
                        Entfernen
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <AdminDialog modus={modus} ziel={ziel} onClose={() => setModus(null)} onFertig={reload} />
    </>
  );
}
